import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { StoreIcon, BookIcon, PlusIcon, ChevronDownIcon, ChevronUpIcon, MapPinIcon, GlobeIcon, MoreVerticalIcon, EditIcon, TrashIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { useRoleTheme } from '../../../hooks/useRoleTheme';
import BackButton from '../../../components/UI/BackButton';
import { bookstores, bookstoreBookLinks } from '../../../data/bookstoresData';
import { allBooks } from '../../../data/booksData';
const ManageStoreListings = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  // Mock vendor books (Elena Richards)
  const vendorBookIds = ['1', '2', '3'];
  const vendorBooks = allBooks.filter(b => vendorBookIds.includes(b.id));
  // Get links for vendor's books
  const vendorLinks = bookstoreBookLinks.filter(link => vendorBookIds.includes(link.bookId));
  // Group links by store
  const storeIds = Array.from(new Set(vendorLinks.map(l => l.bookstoreId)));
  const linkedStores = bookstores.filter(s => storeIds.includes(s.id));
  const [expandedStores, setExpandedStores] = useState<string[]>([]);
  const toggleStore = (storeId: string) => {
    setExpandedStores(prev => prev.includes(storeId) ? prev.filter(id => id !== storeId) : [...prev, storeId]);
  };
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'brick-and-mortar':
        return dk.isDark ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-100 text-emerald-800';
      case 'online-only':
        return dk.isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800';
      case 'hybrid':
        return dk.isDark ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-800';
      case 'pop-up':
        return dk.isDark ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-800';
      case 'specialty':
        return dk.isDark ? 'bg-rose-900/40 text-rose-300' : 'bg-rose-100 text-rose-800';
      default:
        return dk.isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };
  const formatType = (type: string) => {
    return type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };
  const getAvailabilityBadge = (type: string) => {
    switch (type) {
      case 'stocked':
        return {
          label: 'In Stock',
          color: 'bg-green-100 text-green-800'
        };
      case 'online-listing':
        return {
          label: 'Online',
          color: 'bg-blue-100 text-blue-800'
        };
      case 'signed-copy':
        return {
          label: 'Signed',
          color: 'bg-purple-100 text-purple-800'
        };
      case 'pre-order':
        return {
          label: 'Pre-order',
          color: 'bg-amber-100 text-amber-800'
        };
      default:
        return {
          label: 'Available',
          color: 'bg-gray-100 text-gray-800'
        };
    }
  };
  return <div className={`min-h-screen py-8 ${dk.page}`} data-id="element-18868" data-mp="2iak6eoghkv13">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-18869" data-mp="2dj25g0egqug4">
        <div className="mb-6" data-id="element-18870" data-mp="2dh8voc8jzu3w">
          <BackButton data-id="element-18871" data-mp="28kiflowr0yih" />
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4" data-id="element-18872" data-mp="28jz0efyhgo85">
          <div data-id="element-18873" data-mp="24gkifjqzhxs2">
            <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-18874" data-mp="1zjkaf3akqkyb">
              Manage Store Listings
            </h1>
            <p className={`text-sm mt-1 ${dk.textMuted}`} data-id="element-18875" data-mp="1ujxyqyae9crm">
              View and manage which bookstores carry your titles.
            </p>
          </div>
          <Link to="/vendors/store-listings/link" className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${rt.primaryButton}`} data-id="element-18876" data-mp="1pnhb0573x0f2">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-18877" data-mp="3k073cihtu1kc" />
            Link Books to Stores
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-18878" data-mp="tsl6agy3ve5">
          <div className={`p-6 rounded-xl shadow-sm border ${dk.card}`} data-id="element-18879" data-mp="t0v1f64apv9">
            <div className="flex items-center justify-between" data-id="element-18880" data-mp="3i14f4sgu98kl">
              <div data-id="element-18881" data-mp="3n0hbq4xihu0d">
                <p className={`text-sm font-medium ${dk.textMuted}`} data-id="element-18882" data-mp="3mzz7al0p49hd">
                  Total Stores
                </p>
                <p className={`text-2xl font-bold mt-1 ${dk.textPrimary}`} data-id="element-18883" data-mp="39815kv9bdfv8">
                  {linkedStores.length}
                </p>
              </div>
              <div className="p-3 rounded-full bg-indigo-100 text-indigo-600" data-id="element-18884" data-mp="2zby3fpegph8a">
                <StoreIcon className="h-6 w-6" data-id="element-18885" data-mp="34airyrkcqcgw" />
              </div>
            </div>
          </div>
          <div className={`p-6 rounded-xl shadow-sm border ${dk.card}`} data-id="element-18886" data-mp="2qaq4r681aedo">
            <div className="flex items-center justify-between" data-id="element-18887" data-mp="2qbjdgcqjzdyg">
              <div data-id="element-18888" data-mp="2uba0q6gvfby8">
                <p className={`text-sm font-medium ${dk.textMuted}`} data-id="element-18889" data-mp="2ubtm5x5qcih0">
                  Total Book-Store Links
                </p>
                <p className={`text-2xl font-bold mt-1 ${dk.textPrimary}`} data-id="element-18890" data-mp="2bpvsipcj05ej">
                  {vendorLinks.length}
                </p>
              </div>
              <div className="p-3 rounded-full bg-purple-100 text-purple-600" data-id="element-18891" data-mp="2kpilqnv3q4wi">
                <BookIcon className="h-6 w-6" data-id="element-18892" data-mp="2fpczxobijcmc" />
              </div>
            </div>
          </div>
          <div className={`p-6 rounded-xl shadow-sm border ${dk.card}`} data-id="element-18893" data-mp="2um3rxciuxm1x">
            <div className="flex items-center justify-between" data-id="element-18894" data-mp="2pntxx58vcrhw">
              <div data-id="element-18895" data-mp="2pnk4t8sj247z">
                <p className={`text-sm font-medium ${dk.textMuted}`} data-id="element-18896" data-mp="2plzig6gd9rl7">
                  Pending Requests
                </p>
                <p className={`text-2xl font-bold mt-1 ${dk.textPrimary}`} data-id="element-18897" data-mp="2zd3noeg8py2u">2</p>
              </div>
              <div className="p-3 rounded-full bg-amber-100 text-amber-600" data-id="element-18898" data-mp="3dcv7ivtfu8or">
                <StoreIcon className="h-6 w-6" data-id="element-18899" data-mp="38ebl6uaxmuvv" />
              </div>
            </div>
          </div>
        </div>

        {/* Store List */}
        {linkedStores.length > 0 ? <div className="space-y-4" data-id="element-18900" data-mp="25ysuwxfa6b5y">
            {linkedStores.map(store => {
          const storeLinks = vendorLinks.filter(l => l.bookstoreId === store.id);
          const isExpanded = expandedStores.includes(store.id);
          return <div key={store.id} className={`rounded-xl border overflow-hidden transition-all ${dk.card}`} data-id="element-18901" data-mp="2jtlkkoovx7q9">
                  {/* Store Header (Clickable) */}
                  <div className={`p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors`} onClick={() => toggleStore(store.id)} data-id="element-18902" data-mp="2tsb1of8yio5g">
                    <div className="flex items-center gap-4 w-full sm:w-auto mb-4 sm:mb-0" data-id="element-18903" data-mp="13geuinkaonpd">
                      <img src={store.logo} alt={store.name} className="w-12 h-12 rounded-lg object-cover border border-gray-200 dark:border-gray-700" data-id="element-18904" data-mp="18f7ynpdoz2cv" />
                      <div data-id="element-18905" data-mp="r31de8up8cy6">
                        <div className="flex items-center gap-2 mb-1" data-id="element-18906" data-mp="r4cf834l3amn">
                          <h3 className={`font-bold text-lg ${dk.textPrimary}`} data-id="element-18907" data-mp="m5ho8dwgqfrg">
                            {store.name}
                          </h3>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${getTypeColor(store.type)}`} data-id="element-18908" data-mp="1jqdwg91ni2mq">
                            {formatType(store.type)}
                          </span>
                        </div>
                        <div className={`flex items-center text-sm ${dk.textSecondary}`} data-id="element-18909" data-mp="15us0tj0wwklc">
                          {store.type === 'online-only' ? <>
                              <GlobeIcon className="h-3.5 w-3.5 mr-1" data-id="element-18910" data-mp="1thsgysbg3pga" />
                              <span data-id="element-18911" data-mp="1tkmyoc32qw60">Online · {store.country}</span>
                            </> : <>
                              <MapPinIcon className="h-3.5 w-3.5 mr-1" data-id="element-18912" data-mp="1kix6v55a5v7p" />
                              <span data-id="element-18913" data-mp="1pfx4qsrz7rnq">
                                {store.city ? `${store.city}, ` : ''}
                                {store.country}
                              </span>
                            </>}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between w-full sm:w-auto gap-6" data-id="element-18914" data-mp="23fyfczy1mdge">
                      <div className="flex items-center gap-4" data-id="element-18915" data-mp="28dzytlzdsese">
                        <div className="text-center" data-id="element-18916" data-mp="2w4lusew428m1">
                          <span className={`block text-lg font-bold ${dk.textPrimary}`} data-id="element-18917" data-mp="2w32dqm2mrtj7">
                            {storeLinks.length}
                          </span>
                          <span className={`text-xs ${dk.textMuted}`} data-id="element-18918" data-mp="3692ddg0z63tj">
                            Books Linked
                          </span>
                        </div>
                        <span className={`px-2.5 py-1 ${rt.badgeRadius} text-xs font-medium bg-green-100 text-green-800`} data-id="element-18919" data-mp="3k2l5qkbjvw89">
                          Active
                        </span>
                      </div>
                      <button className={`p-1.5 rounded-md ${dk.isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`} data-id="element-18920" data-mp="2nbja6t177ff1">
                        {isExpanded ? <ChevronUpIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-18921" data-mp="2w9be711gz0n4" /> : <ChevronDownIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-18922" data-mp="bl6w8kajz9vg" />}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Content - Books at this store */}
                  {isExpanded && <div className={`border-t p-4 sm:p-6 ${dk.isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`} data-id="element-18923" data-mp="32y75wns4alpk">
                      <h4 className={`text-sm font-semibold mb-4 uppercase tracking-wider ${dk.textMuted}`} data-id="element-18924" data-mp="2u03iflxknev4">
                        Linked Books
                      </h4>
                      <div className="space-y-3" data-id="element-18925" data-mp="2k1ebgo77retn">
                        {storeLinks.map((link, idx) => {
                  const book = vendorBooks.find(b => b.id === link.bookId);
                  if (!book) return null;
                  const badge = getAvailabilityBadge(link.availabilityType);
                  return <div key={idx} className={`flex items-center justify-between p-3 rounded-lg border ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-18926" data-mp="przehz7s49yp" data-mpi={idx}>
                              <div className="flex items-center gap-3" data-id="element-18927" data-mp="2tmh613yvaiv">
                                <img src={book.coverImage} alt={book.title} className="w-8 h-12 object-cover rounded shadow-sm" data-id="element-18928" data-mp="6ug0eh6gs15f" />
                                <div data-id="element-18929" data-mp="gtx791xgcl2z">
                                  <p className={`font-medium text-sm ${dk.textPrimary}`} data-id="element-18930" data-mp="1n8y4rc24lbbo">
                                    {book.title}
                                  </p>
                                  <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-medium ${badge.color}`} data-id="element-18931" data-mp="12i2b5k863ogz">
                                    {badge.label}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2" data-id="element-18932" data-mp="jsm6qltd3bva">
                                <button className={`p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors`} title="Edit Availability" data-id="element-18933" data-mp="eu2nwua2eg75">
                                  <EditIcon className="h-4 w-4" data-id="element-18934" data-mp="oqxlnayikckd" />
                                </button>
                                <button className={`p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors`} title="Remove Link" data-id="element-18935" data-mp="10jox74zhpjg">
                                  <TrashIcon className="h-4 w-4" data-id="element-18936" data-mp="2ciolyh7x4el9" />
                                </button>
                              </div>
                            </div>;
                })}
                      </div>
                    </div>}
                </div>;
        })}
          </div> : <div className={`text-center py-16 rounded-xl border ${dk.card}`} data-id="element-18937" data-mp="2vg8z4r1bloq1">
            <StoreIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-18938" data-mp="2ve4x6emzezse" />
            <h3 className={`text-lg font-medium mb-2 ${dk.textPrimary}`} data-id="element-18939" data-mp="2hggdvb8kc0pg">
              No Store Listings Yet
            </h3>
            <p className={`mb-6 max-w-md mx-auto ${dk.textSecondary}`} data-id="element-18940" data-mp="161kn4qwn68sj">
              You haven't linked any of your books to bookstores yet. Start
              building your retail presence by linking your catalog to stores in
              our directory.
            </p>
            <Link to="/vendors/store-listings/link" className={`inline-flex items-center px-4 py-2 rounded-md font-medium transition-colors ${rt.primaryButton}`} data-id="element-18941" data-mp="1qblwwb71itjy">
              <PlusIcon className="h-4 w-4 mr-2" data-id="element-18942" data-mp="1giet2ury7llz" />
              Link Books to Stores
            </Link>
          </div>}
      </div>
    </div>;
};
export default ManageStoreListings;